import Footer from '@/components/Footer'
import { getAccessTokenPayload } from '@/lib/jwt/decode'
import usePageTracking from '@/lib/segment/hooks/usePageTracking'
import { AppBarProps } from '@mui/material'
import { AppBar, Layout as DefaultLayout, TitlePortal } from 'react-admin'
import { Navigate, useLocation } from 'react-router-dom'
import { AdminDropdown } from './AdminDropdown'
import usePartnerName from './hooks/usePartnerName'
import Menu from './Menu'
import { MultiRoleInfo } from './PartnerInfo'

const PAGE_TITLES: Record<string, string> = {
  '/calendar': 'Calendar',
  '/roles': 'Role Select',
  '/resources': 'Resources',
  '/stack-media': 'Stack Media',
  '/account/general-info': 'Account / General Info',
  '/account/payment-info': 'Account / Payment Info',
  '/earnings/deductions': 'Earnings / Deductions',
  '/earnings/payment-status': 'Earnings / Payment Status',
  '/orders': 'Orders'
}

const NO_LAYOUT_PAGES = ['/update-password']

const getPageTitle = (pathname: string, partnerName: string) => {
  const entry = Object.entries(PAGE_TITLES).find(([key]) =>
    pathname.startsWith(key)
  )
  return entry
    ? partnerName
      ? `${partnerName} / ${entry[1]}`
      : entry[1]
    : null
}

const CustomAppBar = (props: AppBarProps) => {
  const location = useLocation()
  const partnerName = usePartnerName()

  return (
    <AppBar {...props} color="primary">
      <TitlePortal
        variant="h2"
        color="inherit"
        sx={{ fontSize: '1.25rem', fontWeight: 'bold', flex: 1 }}
      >
        <>{getPageTitle(location.pathname, partnerName)}</>
      </TitlePortal>
      <MultiRoleInfo />
      <AdminDropdown />
    </AppBar>
  )
}

const Layout = ({ children }: { children: React.ReactNode }) => {
  usePageTracking()
  const location = useLocation()

  const jwtPayload = getAccessTokenPayload()
  if (
    jwtPayload?.data?.attributes?.must_update_password &&
    !location.pathname.startsWith('/update-password')
  ) {
    return <Navigate to="/update-password" replace />
  }

  const hideLayout = NO_LAYOUT_PAGES.some((path) =>
    location.pathname.startsWith(path)
  )

  if (hideLayout) return <>{children}</>

  return (
    <>
      <DefaultLayout
        menu={Menu}
        appBar={CustomAppBar}
        sx={{
          '& .RaSidebar-fixed': {
            height: 'calc(100vh - 15em)'
          }
        }}
      >
        {children}

        <Footer
          sx={{
            left: 0,
            bottom: 0,
            position: 'fixed',
            width: '100%'
          }}
        />
      </DefaultLayout>
    </>
  )
}

export default Layout
